<template>
  <div class="creditos-page">
    <!-- Header -->
    <div class="prov-header">
      <div>
        <span class="header-eyebrow">B2B</span>
        <h2 class="header-title">Créditos</h2>
      </div>
      <div class="header-actions">
        <button
          v-if="clienteFiltro"
          class="btn-cred btn-cred--ghost"
          :disabled="pdfTodos"
          @click="imprimirCliente(clienteFiltro)"
        >
          <b-spinner v-if="pdfTodos" small />
          <i v-else class="fa-solid fa-print"></i>
          Imprimir créditos del cliente
        </button>
        <button class="btn-cred btn-cred--primary" @click="toggleFormulario">
          <i :class="showForm ? 'fa-solid fa-xmark' : 'fa-solid fa-plus'"></i>
          {{ showForm ? 'Cerrar' : 'Nuevo crédito' }}
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Créditos</span>
        <span class="kpi-value">{{ creditos.length }}</span>
        <span class="kpi-foot">emitidos</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Importe total</span>
        <span class="kpi-value kpi-value--sm">{{ importePorMoneda(creditos) || '--' }}</span>
        <span class="kpi-foot">en créditos</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Clientes</span>
        <span class="kpi-value">{{ resumenClientes.length }}</span>
        <span class="kpi-foot">con crédito</span>
      </div>
    </div>

    <!-- Formulario nuevo crédito -->
    <transition name="slide">
      <div class="form-card" v-if="showForm">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="fa-solid fa-hand-holding-dollar"></i>
          </div>
          <div>
            <h3 class="form-card__title">Nuevo crédito</h3>
            <p class="form-card__subtitle">Se asocia a un cliente y puede tener varias líneas</p>
          </div>
        </div>

        <div class="form-row">
          <div class="field field--cliente">
            <label class="field__label">Cliente</label>
            <v-select
              v-model="clienteSel"
              :options="clientes"
              placeholder="Seleccionar cliente..."
              class="select-styled"
            />
          </div>
          <div class="field">
            <label class="field__label">Moneda</label>
            <v-select
              v-model="monedaSel"
              :options="monedas"
              label="label"
              :clearable="false"
              class="select-styled"
            />
          </div>
        </div>

        <!-- Líneas -->
        <div class="lineas">
          <div class="linea linea--head">
            <span>Item description</span>
            <span>SKU</span>
            <span class="text-center">Unidades</span>
            <span class="text-right">Precio/ud</span>
            <span>Razón del crédito</span>
            <span></span>
          </div>
          <div class="linea" v-for="(l, i) in lineas" :key="i">
            <b-form-input size="sm" v-model="l.description" placeholder="Producto" />
            <b-form-input size="sm" v-model="l.sku" placeholder="HL-EX-001" />
            <b-form-input size="sm" v-model="l.qty" type="number" min="0" class="text-center" />
            <b-form-input size="sm" v-model="l.unitPrice" type="number" min="0" step="0.01" class="text-right" />
            <b-form-input size="sm" v-model="l.razon" placeholder="Damaged on arrival" />
            <button class="action-btn action-btn--delete" title="Quitar línea" @click="lineas.splice(i, 1)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
          <button class="btn-add-linea" @click="agregarLinea">
            <i class="fa-solid fa-plus mr-25"></i> Añadir línea
          </button>
        </div>

        <div class="form-row">
          <div class="field field--cliente">
            <label class="field__label">Notas</label>
            <b-form-input size="sm" v-model="notas" placeholder="Opcional" />
          </div>
        </div>

        <div class="form-card__footer">
          <span class="total-preview">
            Total crédito:
            <b>{{ monedaSel.simbolo }}{{ totalNuevo.toFixed(2) }}</b>
          </span>
          <button
            class="btn-cred btn-cred--primary"
            :disabled="guardando || !clienteSel || !lineasValidas.length"
            @click="guardarCredito"
          >
            <b-spinner v-if="guardando" small />
            <i v-else class="fa-solid fa-check"></i>
            Crear crédito
          </button>
        </div>
      </div>
    </transition>

    <!-- Resumen por cliente -->
    <div class="list-card mb-2" v-if="resumenClientes.length">
      <div class="list-card__header list-card__header--slim">
        <h3 class="list-card__title">Saldo por cliente</h3>
      </div>
      <div class="saldo-grid">
        <button
          v-for="rc in resumenClientes"
          :key="rc.customerId"
          class="saldo-chip"
          :class="{ active: clienteFiltro === rc.customerId }"
          :title="clienteFiltro === rc.customerId ? 'Quitar filtro' : 'Ver solo sus créditos'"
          @click="clienteFiltro = clienteFiltro === rc.customerId ? null : rc.customerId"
        >
          <span class="saldo-chip__nombre">{{ rc.nombre }}</span>
          <span class="saldo-chip__total">{{ rc.totalStr }}</span>
          <span class="saldo-chip__n">{{ rc.n }} crédito{{ rc.n === 1 ? '' : 's' }}</span>
        </button>
      </div>
    </div>

    <!-- Listado -->
    <div class="list-card">
      <div class="list-card__header">
        <h3 class="list-card__title">Listado</h3>
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
          <input
            type="text"
            class="search-box__input"
            placeholder="Buscar por cliente, número o SKU..."
            v-model="buscar"
          />
        </div>
      </div>

      <div class="table-wrapper">
        <b-table
          :items="creditosFiltrados"
          :fields="columnas"
          class="mb-0"
          hover
          responsive
          show-empty
          :empty-text="cargando ? 'Cargando...' : 'No hay créditos'"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(toggle)="data">
            <button
              class="action-btn"
              :title="data.detailsShowing ? 'Ocultar líneas' : 'Ver líneas'"
              @click="data.toggleDetails()"
            >
              <i class="fa-solid" :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </template>

          <template #cell(numero)="data">
            <span class="cell-numero">{{ data.item.numero }}</span>
          </template>

          <template #cell(fecha)="data">
            <span class="cell-mono">{{ formatFecha(data.item.fecha) }}</span>
          </template>

          <template #cell(cliente)="data">
            <span
              class="cell-cliente cell-cliente--link"
              @click="$router.push({ name: 'customer-profile', params: { id: data.item.customerId } })"
            >{{ nombreCliente(data.item) }}</span>
          </template>

          <template #cell(lineas)="data">
            <span class="cell-mono">{{ data.item.items.length }} línea{{ data.item.items.length === 1 ? '' : 's' }} · {{ totalUds(data.item) }} uds</span>
          </template>

          <template #cell(total)="data">
            <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ Number(data.item.total).toFixed(2) }}</span>
            <span class="cell-gastado" v-if="Number(data.item.gastado)">
              quedan {{ data.item.simbolo }}{{ (Number(data.item.total) - Number(data.item.gastado)).toFixed(2) }}
            </span>
          </template>

          <template #cell(acciones)="data">
            <div class="cell-actions">
              <button
                class="action-btn action-btn--pdf"
                title="Imprimir"
                :disabled="pdfId === data.item.id"
                @click="verPdf(data.item)"
              >
                <b-spinner v-if="pdfId === data.item.id" small />
                <i v-else class="fa-solid fa-print"></i>
              </button>
              <button class="action-btn action-btn--delete" title="Eliminar" @click="eliminar(data.item)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </template>

          <template #row-details="data">
            <div class="detalle">
              <div class="detalle__row detalle__row--head">
                <span>Item description</span>
                <span>SKU</span>
                <span class="text-center">Uds</span>
                <span class="text-right">Precio/ud</span>
                <span>Razón</span>
              </div>
              <div v-for="(it, i) in data.item.items" :key="i" class="detalle__row">
                <span class="detalle__name">{{ it.description }}</span>
                <span class="cell-mono">{{ it.sku }}</span>
                <span class="cell-mono text-center">{{ it.qty }}</span>
                <span class="cell-mono text-right">{{ data.item.simbolo }}{{ Number(it.unitPrice).toFixed(2) }}</span>
                <span class="detalle__razon">{{ it.razon }}</span>
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <div class="list-card__footer">
        <span class="footer-count">{{ creditosFiltrados.length }} créditos</span>
      </div>
    </div>
  </div>
</template>

<script>
import { BTable, BSpinner, BFormInput } from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@axios'

const lineaVacia = () => ({
  description: '', sku: '', qty: 1, unitPrice: '', razon: '',
})

export default {
  components: {
    BTable, BSpinner, BFormInput, vSelect,
  },
  data() {
    return {
      creditos: [],
      clientes: [],
      cargando: true,
      buscar: '',
      clienteFiltro: null,
      showForm: false,
      guardando: false,
      pdfId: null,
      pdfTodos: false,
      // Formulario
      clienteSel: null,
      monedas: [
        { label: 'GBP (£)', code: 'GBP', simbolo: '£' },
        { label: 'EUR (€)', code: 'EUR', simbolo: '€' },
        { label: 'USD ($)', code: 'USD', simbolo: '$' },
      ],
      monedaSel: { label: 'GBP (£)', code: 'GBP', simbolo: '£' },
      lineas: [lineaVacia()],
      notas: '',
      columnas: [
        { key: 'toggle', label: '', thStyle: { width: '46px' } },
        { key: 'numero', label: 'Nº', sortable: true },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'cliente', label: 'Cliente' },
        { key: 'lineas', label: 'Líneas' },
        { key: 'total', label: 'Total', sortable: true },
        { key: 'acciones', label: '', thStyle: { width: '100px' } },
      ],
    }
  },
  computed: {
    lineasValidas() {
      return this.lineas.filter(l => (Number(l.qty) > 0) && (l.description.trim() || l.sku.trim()))
    },
    totalNuevo() {
      return this.lineasValidas.reduce((acc, l) => acc + Number(l.qty) * (Number(l.unitPrice) || 0), 0)
    },
    resumenClientes() {
      const porCliente = {}
      this.creditos.forEach(cr => {
        if (!porCliente[cr.customerId]) {
          porCliente[cr.customerId] = {
            customerId: cr.customerId, nombre: this.nombreCliente(cr), n: 0, totales: {},
          }
        }
        porCliente[cr.customerId].n += 1
        const s = cr.simbolo || '£'
        // Saldo disponible: lo emitido menos lo ya gastado de cada crédito
        const disponible = Number(cr.total) - (Number(cr.gastado) || 0)
        porCliente[cr.customerId].totales[s] = (porCliente[cr.customerId].totales[s] || 0) + disponible
      })
      return Object.values(porCliente).map(rc => ({
        ...rc,
        totalStr: Object.keys(rc.totales).map(s => `${s}${(Math.round(rc.totales[s] * 100) / 100)}`).join(' + '),
      }))
    },
    creditosFiltrados() {
      let lista = this.creditos
      if (this.clienteFiltro) lista = lista.filter(cr => cr.customerId === this.clienteFiltro)

      const q = this.buscar.toLowerCase().trim()
      if (!q) return lista
      return lista.filter(cr => {
        const campos = [
          cr.numero, this.nombreCliente(cr), cr.email,
          ...cr.items.map(it => it.sku), ...cr.items.map(it => it.description),
        ]
        return campos.some(c => c && c.toString().toLowerCase().includes(q))
      })
    },
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Créditos',
        variant,
        solidColor: true,
      })
    },
    nombreCliente(cr) {
      return cr.companyName || [cr.nombre, cr.apellidos].filter(Boolean).join(' ') || cr.email || '—'
    },
    formatFecha(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    totalUds(cr) {
      return (cr.items || []).reduce((acc, it) => acc + (Number(it.qty) || 0), 0)
    },
    importePorMoneda(lista) {
      const porMoneda = {}
      lista.forEach(cr => {
        const s = cr.simbolo || '£'
        porMoneda[s] = (porMoneda[s] || 0) + (Number(cr.total) || 0)
      })
      return Object.keys(porMoneda).map(s => `${s}${(Math.round(porMoneda[s] * 100) / 100)}`).join(' + ')
    },
    toggleFormulario() {
      if (this.showForm) this.limpiarFormulario()
      this.showForm = !this.showForm
    },
    agregarLinea() {
      this.lineas.push(lineaVacia())
    },
    limpiarFormulario() {
      this.clienteSel = null
      this.lineas = [lineaVacia()]
      this.notas = ''
    },
    guardarCredito() {
      this.guardando = true
      axios
        .post(`/customers/${this.clienteSel.id}/creditos`, {
          moneda: this.monedaSel.code,
          simbolo: this.monedaSel.simbolo,
          items: this.lineasValidas,
          notas: this.notas,
        })
        .then(({ data }) => {
          this.notificar(`Crédito ${data.numero} creado`)
          this.limpiarFormulario()
          this.showForm = false
          this.load()
        })
        .catch(() => { this.notificar('No se pudo crear el crédito', 'danger') })
        .finally(() => { this.guardando = false })
    },
    eliminar(cr) {
      this.$bvModal
        .msgBoxConfirm(`Se eliminará el crédito ${cr.numero} de ${this.nombreCliente(cr)}.`, {
          title: 'Eliminar crédito',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          centered: true,
        })
        .then(ok => {
          if (!ok) return
          axios
            .delete(`/customers/creditos/${cr.id}`)
            .then(() => {
              this.creditos = this.creditos.filter(c => c.id !== cr.id)
              this.notificar(`Crédito ${cr.numero} eliminado`)
            })
            .catch(() => { this.notificar('No se pudo eliminar', 'danger') })
        })
    },
    verPdf(cr) {
      this.pdfId = cr.id
      axios
        .get(`/customers/creditos/${cr.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo generar el PDF', 'danger') })
        .finally(() => { this.pdfId = null })
    },
    imprimirCliente(customerId) {
      this.pdfTodos = true
      axios
        .get('/customers/creditos/pdf/todos', { responseType: 'blob', params: { customerId } })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo generar el PDF', 'danger') })
        .finally(() => { this.pdfTodos = false })
    },
    load() {
      this.cargando = true
      axios
        .get('/customers/creditos')
        .then(({ data }) => { this.creditos = data || [] })
        .catch(() => {})
        .finally(() => { this.cargando = false })
      axios
        .get('/customers')
        .then(({ data }) => {
          this.clientes = (data || []).map(c => ({
            ...c,
            label: [c.nombre, c.apellidos].filter(Boolean).join(' ')
              + (c.companyName ? ` — ${c.companyName}` : ''),
          }))
        })
        .catch(() => {})
    },
  },
  mounted() {
    axios.post('/uso', { seccion: 'creditos' }).catch(() => {})
    if (this.$route.query.cliente) this.clienteFiltro = Number(this.$route.query.cliente)
    this.load()
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.creditos-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.prov-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.btn-cred {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--primary {
    background: $primary;
    color: white;
    &:hover { background: #2563EB; }
    &:disabled { opacity: 0.6; cursor: default; }
  }

  &--ghost {
    background: $card-bg;
    border: 1px solid $border;
    color: $text-muted;
    &:hover { color: $primary; border-color: $primary; }
    &:disabled { opacity: 0.6; cursor: default; }
  }
}

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--sm { font-size: 16px; margin-top: 8px; }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

// Formulario
.form-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  margin-bottom: 20px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 22px 24px 8px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    background: rgba($primary, 0.1);
    color: $primary;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &__subtitle {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: $text-muted;
    margin: 2px 0 0;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 18px;
    padding: 8px 24px 20px;
  }
}

.total-preview {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  color: $text-muted;

  b { color: $text; font-size: 15px; }
}

.form-row {
  display: flex;
  gap: 14px;
  padding: 8px 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 160px;

  &--cliente { flex: 1; }

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: $text-muted;
    margin: 0;
  }
}

.lineas {
  padding: 8px 24px;
}

.linea {
  display: grid;
  grid-template-columns: 1fr 140px 80px 90px 1fr 34px;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;

  &--head {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: $text-muted;
    margin-bottom: 2px;
  }
}

.btn-add-linea {
  border: 1px dashed $border;
  background: transparent;
  border-radius: $radius-sm;
  width: 100%;
  padding: 7px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { color: $primary; border-color: $primary; }
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
  max-height: 1200px;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
}

// Saldo por cliente
.saldo-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 22px;
}

.saldo-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid $border;
  border-radius: $radius-sm;
  background: transparent;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover { border-color: $primary; }

  &.active {
    border-color: $primary;
    background: rgba($primary, 0.05);
  }

  &__nombre {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: $text;
  }

  &__total {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    font-weight: 700;
    color: $primary;
    margin: 2px 0;
  }

  &__n {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: $text-muted;
  }
}

// Listado
.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 22px;
    border-bottom: 1px solid $border;

    &--slim { padding: 14px 22px 0; border-bottom: none; }
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &__footer {
    padding: 12px 22px;
    border-top: 1px solid $border;
  }
}

.mb-2 { margin-bottom: 20px; }

.search-box {
  position: relative;
  width: 280px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

// Los ::v-deep van bajo .creditos-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.creditos-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 16px;
  background: transparent;
}

.creditos-page ::v-deep .table-row td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 13px;
}

.cell-numero {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: $text;
}

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text;
}

.cell-total { font-weight: 700; }

.cell-gastado {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #F59E0B;
  margin-top: 2px;
}

.cell-cliente {
  font-weight: 600;

  &--link {
    color: #7367f0;
    cursor: pointer;
  }
}

.cell-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s ease;

  &--pdf:hover { background: rgba($primary, 0.1); color: $primary; }
  &--delete:hover { background: rgba($danger, 0.1); color: $danger; }
}

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

// Detalle de líneas
.detalle {
  background: $bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 10px 14px;

  &__row {
    display: grid;
    grid-template-columns: 1fr 140px 60px 90px 1fr;
    gap: 10px;
    align-items: center;
    padding: 5px 0;
    border-bottom: 1px solid $border;

    &:last-child { border-bottom: none; }

    &--head {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $text-muted;
      border-bottom: 1px solid $border;
    }
  }

  &__name {
    font-size: 12px;
    font-weight: 600;
    color: $text;
  }

  &__razon {
    font-size: 12px;
    color: $text-muted;
  }
}

@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: 1fr; }
  .prov-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .form-row { flex-direction: column; }
  .linea { grid-template-columns: 1fr 1fr; }
  .linea--head { display: none; }
  .search-box { width: 100%; }
}

// Dark mode
.dark-layout {
  .creditos-page {
    .header-title { color: #E2E8F0; }

    .kpi-card, .form-card, .list-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }
    .form-card__title, .list-card__title { color: #E2E8F0; }
    .list-card__header, .list-card__footer { border-color: #334155; }

    .btn-cred--ghost { background: #1E293B; border-color: #334155; }

    .saldo-chip {
      border-color: #475569;
      &__nombre { color: #E2E8F0; }
    }

    .search-box__input {
      border-color: #334155;
      color: #E2E8F0;
      &::placeholder { color: #475569; }
    }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .cell-numero, .cell-mono { color: #E2E8F0; }
    .cell-cliente { color: #E2E8F0; }
    .cell-cliente--link { color: #7367f0; }

    .detalle { background: #0F172A; border-color: #334155; }
    .detalle__row { border-color: #334155; }
    .detalle__name { color: #E2E8F0; }

    .btn-add-linea { border-color: #475569; color: #94A3B8; }
  }
}
</style>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
</style>
